import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, handleSupabaseError, logger, addDaysToBaliKey } from '@/lib';
import { getCurrentAdminDetails } from '@/lib/admin-details';
import { logActivity } from '@/utils';
import { useInvalidateMemberProfile } from './useInvalidateMemberProfile';
import type { MembershipFreeze } from '@/types';
import { MEMBERSHIP_QUERY_KEYS } from '@/constants';

interface CreateFreezeInput {
  membershipId: string;
  memberId: string;
  startDate: string;
  endDate: string;
  fee: number;
  reason?: string;
}

interface UpdateFreezeInput {
  freezeId: string;
  membershipId: string;
  memberId: string;
  startDate: string;
  endDate: string;
  fee: number;
  reason?: string;
}

interface DeleteFreezeInput {
  freezeId: string;
  membershipId: string;
  memberId: string;
}

async function getCurrentAdmin() {
  const admin = await getCurrentAdminDetails(supabase);
  return {
    adminId: admin.id,
    adminName: admin.display_name,
    adminEmail: admin.email,
  };
}

/**
 * Recalculate membership end_date based on original_end_date + total freeze days.
 * Called after every freeze add/edit/delete.
 */
async function recalculateEndDate(membershipId: string) {
  // Get original_end_date
  const { data: membership, error: mErr } = await supabase
    .from('memberships')
    .select('original_end_date')
    .eq('id', membershipId)
    .single();

  if (mErr || !membership?.original_end_date) {
    throw new Error('Failed to fetch membership for end_date recalculation');
  }

  // Sum all freeze durations
  const { data: freezes, error: fErr } = await supabase
    .from('membership_freezes')
    .select('duration_days')
    .eq('membership_id', membershipId);

  if (fErr) throw new Error('Failed to fetch freezes for recalculation');

  const totalFreezeDays = (freezes || []).reduce((sum, f) => sum + f.duration_days, 0);

  // Calculate new end_date. Both operands are Bali date keys (YYYY-MM-DD), so
  // shift on the date grid — never via `new Date(key + 'T00:00:00')`, which
  // parses as *browser-local* midnight and then reads the UTC side back out of
  // toISOString(). In WITA that pair lands a day early on every call, even at
  // zero freeze days.
  const newEndDate = addDaysToBaliKey(membership.original_end_date, totalFreezeDays);

  // Update membership
  const { error: uErr } = await supabase
    .from('memberships')
    .update({ end_date: newEndDate })
    .eq('id', membershipId);

  if (uErr) throw new Error('Failed to update membership end_date');

  return newEndDate;
}

// Both boundary dates are frozen days: end_date is the LAST frozen day, and the
// member regains access the day after. Hence the +1 (a same-day freeze is 1 day).
function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Fetch all freezes for a membership
 */
export function useMembershipFreezes(membershipId: string) {
  return useQuery({
    queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZES_PREFIX, membershipId],
    enabled: Boolean(membershipId),
    queryFn: async () => {
      if (!membershipId) return [] as MembershipFreeze[];

      const { data, error } = await supabase
        .from('membership_freezes')
        .select('*')
        .eq('membership_id', membershipId)
        .order('start_date', { ascending: true });

      if (error) handleSupabaseError(error, { operation: 'fetching membership freezes' });
      return (data || []) as MembershipFreeze[];
    },
  });
}

/**
 * Fetch total freeze days per membership (for membership list summary)
 */
export function useBulkMembershipFreezeSummary(membershipIds: string[]) {
  return useQuery({
    queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZE_SUMMARY_PREFIX, membershipIds],
    enabled: membershipIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('membership_freezes')
        .select('membership_id, duration_days')
        .in('membership_id', membershipIds);

      if (error) handleSupabaseError(error, { operation: 'fetching freeze summaries' });

      // Aggregate: { membershipId: totalDays }
      const summary: Record<string, number> = {};
      for (const f of data || []) {
        summary[f.membership_id] = (summary[f.membership_id] || 0) + f.duration_days;
      }
      return summary;
    },
    // Tab-click on the Memberships tab remounts this hook;
    // refetch each time so the row badges show fresh freeze totals.
    refetchOnMount: 'always',
  });
}

/**
 * Create a new freeze
 */
export function useCreateMembershipFreeze() {
  const queryClient = useQueryClient();
  const invalidateMemberProfile = useInvalidateMemberProfile();

  return useMutation({
    mutationFn: async (input: CreateFreezeInput) => {
      const admin = await getCurrentAdmin();
      const durationDays = calculateDuration(input.startDate, input.endDate);

      if (durationDays <= 0) throw new Error('End date cannot be before start date');

      const { data, error } = await supabase
        .from('membership_freezes')
        .insert({
          membership_id: input.membershipId,
          start_date: input.startDate,
          end_date: input.endDate,
          duration_days: durationDays,
          fee: input.fee,
          reason: input.reason || null,
          created_by_name: admin.adminName,
          created_by_email: admin.adminEmail,
        })
        .select()
        .single();

      if (error) handleSupabaseError(error, { operation: 'creating membership freeze' });

      // Recalculate membership end_date
      const newEndDate = await recalculateEndDate(input.membershipId);

      // Log activity
      logActivity(
        'membership_freeze_added',
        'membership',
        input.membershipId,
        {},
        {
          freeze_id: data.id,
          start_date: input.startDate,
          end_date: input.endDate,
          duration_days: durationDays,
          fee: input.fee,
          reason: input.reason || null,
          new_membership_end_date: newEndDate,
          admin_name: admin.adminName,
          admin_email: admin.adminEmail,
        },
        input.memberId
      ).catch(() => {});

      // Fire-and-forget: generate freeze receipt in background
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.access_token) return;
        fetch(`/api/generate-freeze-receipt?id=${data.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        })
          .then(async (response) => {
            if (response.ok) {
              logger.info('Background freeze receipt generation successful', { freezeId: data.id }, 'useMembershipFreezes');
            } else {
              const errorData = await response.json().catch(() => ({ error: 'Invalid response' }));
              logger.error('Background freeze receipt generation failed:', errorData);
            }
          })
          .catch(err => {
            logger.error('Background freeze receipt generation error:', err);
          });
      });

      return data as MembershipFreeze;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZES_PREFIX, variables.membershipId] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZE_SUMMARY_PREFIX] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.ALL] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.BY_MEMBER_PREFIX] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.ACTIVITY_LOGS_PREFIX, variables.membershipId] });
      invalidateMemberProfile(variables.memberId);
    },
  });
}

/**
 * Update an existing freeze
 */
export function useUpdateMembershipFreeze() {
  const queryClient = useQueryClient();
  const invalidateMemberProfile = useInvalidateMemberProfile();

  return useMutation({
    mutationFn: async (input: UpdateFreezeInput) => {
      const admin = await getCurrentAdmin();
      const durationDays = calculateDuration(input.startDate, input.endDate);

      if (durationDays <= 0) throw new Error('End date cannot be before start date');

      // Fetch current freeze data for change log "from" values
      const { data: oldFreeze } = await supabase
        .from('membership_freezes')
        .select('start_date, end_date, duration_days, fee, reason')
        .eq('id', input.freezeId)
        .single();

      const { data, error } = await supabase
        .from('membership_freezes')
        .update({
          start_date: input.startDate,
          end_date: input.endDate,
          duration_days: durationDays,
          fee: input.fee,
          reason: input.reason || null,
        })
        .eq('id', input.freezeId)
        .select()
        .single();

      if (error) handleSupabaseError(error, { operation: 'updating membership freeze' });

      const newEndDate = await recalculateEndDate(input.membershipId);

      logActivity(
        'membership_freeze_updated',
        'membership',
        input.membershipId,
        {
          start_date: { from: oldFreeze?.start_date ?? null, to: input.startDate },
          end_date: { from: oldFreeze?.end_date ?? null, to: input.endDate },
          duration_days: { from: oldFreeze?.duration_days ?? null, to: durationDays },
          fee: { from: oldFreeze?.fee ?? null, to: input.fee },
        },
        {
          freeze_id: input.freezeId,
          new_membership_end_date: newEndDate,
          admin_name: admin.adminName,
          admin_email: admin.adminEmail,
        },
        input.memberId
      ).catch(() => {});

      // Fire-and-forget: regenerate receipt with updated data
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.access_token) return;
        fetch(`/api/generate-freeze-receipt?id=${input.freezeId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      });

      return data as MembershipFreeze;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZES_PREFIX, variables.membershipId] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZE_SUMMARY_PREFIX] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.ALL] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.BY_MEMBER_PREFIX] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.ACTIVITY_LOGS_PREFIX, variables.membershipId] });
      invalidateMemberProfile(variables.memberId);
    },
  });
}

/**
 * Delete a freeze
 */
export function useDeleteMembershipFreeze() {
  const queryClient = useQueryClient();
  const invalidateMemberProfile = useInvalidateMemberProfile();

  return useMutation({
    mutationFn: async (input: DeleteFreezeInput) => {
      const admin = await getCurrentAdmin();

      // Get freeze data before deleting (for activity log)
      const { data: freeze } = await supabase
        .from('membership_freezes')
        .select('*')
        .eq('id', input.freezeId)
        .single();

      const { error } = await supabase
        .from('membership_freezes')
        .delete()
        .eq('id', input.freezeId);

      if (error) handleSupabaseError(error, { operation: 'deleting membership freeze' });

      const newEndDate = await recalculateEndDate(input.membershipId);

      if (freeze) {
        logActivity(
          'membership_freeze_deleted',
          'membership',
          input.membershipId,
          {
            start_date: { from: freeze.start_date, to: null },
            end_date: { from: freeze.end_date, to: null },
            duration_days: { from: freeze.duration_days, to: null },
          },
          {
            freeze_id: input.freezeId,
            deleted_freeze: {
              start_date: freeze.start_date,
              end_date: freeze.end_date,
              duration_days: freeze.duration_days,
              fee: freeze.fee,
              reason: freeze.reason,
            },
            new_membership_end_date: newEndDate,
            admin_name: admin.adminName,
            admin_email: admin.adminEmail,
          },
          input.memberId
        ).catch(() => {});
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZES_PREFIX, variables.membershipId] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZE_SUMMARY_PREFIX] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.ALL] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.BY_MEMBER_PREFIX] });
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.ACTIVITY_LOGS_PREFIX, variables.membershipId] });
      invalidateMemberProfile(variables.memberId);
    },
  });
}

/**
 * Generate a freeze receipt (or regenerate)
 */
export function useGenerateFreezeReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ freezeId }: { freezeId: string; membershipId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('You must be logged in.');

      const response = await fetch(`/api/generate-freeze-receipt?id=${freezeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate receipt' }));
        throw new Error(errorData.error || 'Failed to generate receipt');
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_QUERY_KEYS.FREEZES_PREFIX, variables.membershipId] });
    },
  });
}

/**
 * Download a freeze receipt (get signed URL and open in new tab)
 */
export function useFreezeReceiptDownload() {
  return useMutation({
    mutationFn: async ({ freezeId }: { freezeId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('You must be logged in.');

      const response = await fetch(`/api/freeze-receipt-download?id=${freezeId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to download receipt' }));
        throw new Error(errorData.error || 'Failed to download receipt');
      }

      const data = await response.json();
      window.open(data.downloadUrl, '_blank');
      return data;
    },
  });
}
